import React from 'react';
import { Tooltip, Typography } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import CheckboxCell from 'components/EqualisationElements/CheckboxCell';
import { AddCommitmentsToEqualisationFormValues } from './../FormWrapper';
import { CalendarDay, numberToCurrencyString } from 'further-ui/utils';
import { CurrencyCode } from 'further-types/lp/vehicle';
import Table, { type Column as TableColumn } from 'further-ui/components/Table';
import AvatarWithName from 'components/new/AvatarWithName';
import NumberInput from 'further-ui/components/NumberInput';
import TooltipTriggerText from 'components/new/TooltipTriggerText';

interface PreviouslyClosedCommitmentsTableProps {
  previouslyClosedCommitments: Array<{
    id: string;
    investorName: string;
    isCapitalContributionRuleOverride?: boolean;
    isCarryPartnerCommitment?: boolean;
    totalCommitmentLcy: number;
    loanCommitmentLcy?: number;
    capitalCommitmentLcy?: number;
    equalisationDate?: CalendarDay | null;
    existingCapitalCommitmentLcy?: number;
    existingLoanCommitmentLcy?: number;
    existingTotalCommitmentLcy?: number;
  }>;
  baseCurrency?: CurrencyCode;
  capitalCommitmentIsEditable: (
    isCapitalContributionRuleOverride: boolean | undefined,
    isCarryPartnerCommitment: boolean | undefined,
  ) => boolean;
  capitalCommitmentOnChange: (
    index: number,
    value: number,
    loanCommitmentLcy: number,
  ) => void;
  loanCommitmentIsEditable: (
    isCapitalContributionRuleOverride: boolean | undefined,
    isCarryPartnerCommitment: boolean | undefined,
  ) => boolean;
  loanCommitmentOnChange: (
    index: number,
    value: number,
    capitalCommitmentLcy: number,
  ) => void;
  capitalContributionRuleOverrideOnChange: (
    index: number,
    value: boolean,
    totalCommitmentLcy: number,
    isCarryPartnerCommitment: boolean | undefined,
  ) => void;
  showOverrideColumn: boolean;
  showOverrideCheckbox: (
    isCarryPartnerCommitment: boolean | undefined,
  ) => boolean;
}

const PreviouslyClosedCommitmentsTable: React.FC<
  PreviouslyClosedCommitmentsTableProps
> = ({
  previouslyClosedCommitments,
  baseCurrency,
  capitalCommitmentIsEditable,
  capitalCommitmentOnChange,
  loanCommitmentIsEditable,
  loanCommitmentOnChange,
  capitalContributionRuleOverrideOnChange,
  showOverrideColumn,
  showOverrideCheckbox,
}) => {
  const { getValues, control, watch } =
    useFormContext<AddCommitmentsToEqualisationFormValues>();
  const formCommitments = watch('commitments');

  const isCPAndHasNewTotalCommitmentValue = (index: number) => {
    const commitment = getValues(`commitments.${index}`);
    return !!(
      commitment?.isCarryPartnerCommitment &&
      commitment?.existingTotalCommitmentLcy !== commitment?.totalCommitmentLcy
    );
  };

  type CommitmentRow = (typeof previouslyClosedCommitments)[number];

  const getOriginalIndex = (row: CommitmentRow) =>
    formCommitments?.findIndex((c) => c.id === row.id) ?? -1;

  const columns: Array<TableColumn<CommitmentRow>> = [
    {
      key: 'investorName',
      label: 'Investor name',
      width: 250,
      render: (row) => (
        <AvatarWithName avatarSize="lg" name={row.investorName} />
      ),
    },
    {
      key: 'equalisationDate',
      label: 'Close date',
      width: 120,
      render: (row) => row.equalisationDate?.toLocalDateLabel() ?? '',
    },
    {
      key: 'existingTotalCommitmentLcy',
      label: 'Total commitment (current)',
      width: 210,
      render: (row) =>
        numberToCurrencyString(row.existingTotalCommitmentLcy, {
          currency: baseCurrency,
        }),
    },
    {
      key: 'existingCapitalCommitmentLcy',
      label: 'Capital contribution (current)',
      width: 220,
      render: (row) =>
        numberToCurrencyString(row.existingCapitalCommitmentLcy, {
          currency: baseCurrency,
        }),
    },
    {
      key: 'existingLoanCommitmentLcy',
      label: 'Loan commitment (current)',
      width: 210,
      render: (row) =>
        numberToCurrencyString(row.existingLoanCommitmentLcy, {
          currency: baseCurrency,
        }),
    },
    {
      key: 'capitalCommitmentLcy',
      label: 'Capital contribution (new)',
      width: 220,
      render: (row) => {
        const originalIndex = getOriginalIndex(row);
        return (
          <Controller
            control={control}
            name={`commitments.${originalIndex}.capitalCommitmentLcy`}
            render={({ field: { value }, fieldState: { error } }) => (
              <NumberInput
                value={value ?? undefined}
                onChange={(newValue: number | null) => {
                  if (newValue === undefined || newValue === null) {
                    return;
                  }
                  capitalCommitmentOnChange(
                    originalIndex,
                    newValue,
                    row.loanCommitmentLcy ?? 0,
                  );
                }}
                min={0}
                decimals={2}
                helperText={error?.message?.toString()}
                error={!!error}
                currencyStartAdornment
                currency={baseCurrency}
                fullWidth
                clearZeroValueOnFocus
                disabled={
                  !capitalCommitmentIsEditable(
                    row.isCapitalContributionRuleOverride ?? false,
                    row.isCarryPartnerCommitment,
                  )
                }
              />
            )}
          />
        );
      },
    },
    {
      key: 'loanCommitmentLcy',
      label: 'Loan commitment (new)',
      width: 220,
      render: (row) => {
        const originalIndex = getOriginalIndex(row);
        return (
          <Controller
            control={control}
            name={`commitments.${originalIndex}.loanCommitmentLcy`}
            render={({ field: { value }, fieldState: { error } }) => (
              <NumberInput
                value={value ?? undefined}
                onChange={(newValue: number | null) => {
                  if (newValue === undefined || newValue === null) {
                    return;
                  }
                  loanCommitmentOnChange(
                    originalIndex,
                    newValue,
                    row.capitalCommitmentLcy ?? 0,
                  );
                }}
                min={0}
                decimals={2}
                helperText={error?.message?.toString()}
                error={!!error}
                currencyStartAdornment
                currency={baseCurrency}
                fullWidth
                clearZeroValueOnFocus
                disabled={
                  !loanCommitmentIsEditable(
                    row.isCapitalContributionRuleOverride ?? false,
                    row.isCarryPartnerCommitment,
                  )
                }
              />
            )}
          />
        );
      },
    },
    {
      key: 'totalCommitmentLcy',
      label: 'Total commitment (new)',
      width: 220,
      render: (row) => {
        const originalIndex = getOriginalIndex(row);
        return (
          <Controller
            control={control}
            name={`commitments.${originalIndex}.totalCommitmentLcy`}
            render={({ field: { value }, fieldState: { error } }) => {
              const tooltipText =
                error?.message ||
                (isCPAndHasNewTotalCommitmentValue(originalIndex) &&
                  "The carried interest partner's total commitment is planned to change in this close. If this is unintended, update the capital contribution or loan commitment so the new total matches the existing total.");
              const text = numberToCurrencyString(value, {
                currency: baseCurrency,
              });
              return (
                <>
                  <Tooltip title={tooltipText} arrow placement="top">
                    <Typography variant="inherit">
                      {tooltipText ? (
                        <TooltipTriggerText variant="inherit" content={text} />
                      ) : (
                        text
                      )}
                    </Typography>
                  </Tooltip>
                </>
              );
            }}
          />
        );
      },
    },
    ...(showOverrideColumn
      ? ([
          {
            key: 'isCapitalContributionRuleOverride',
            width: 150,
            label: 'Manual override',
            render: (row) => {
              if (!showOverrideCheckbox(row.isCarryPartnerCommitment))
                return null;
              const originalIndex = getOriginalIndex(row);
              return (
                <CheckboxCell
                  control={control}
                  name={`commitments.${originalIndex}.isCapitalContributionRuleOverride`}
                  onChange={(value) => {
                    capitalContributionRuleOverrideOnChange(
                      originalIndex,
                      value,
                      row.totalCommitmentLcy,
                      row.isCarryPartnerCommitment,
                    );
                  }}
                />
              );
            },
          },
        ] satisfies Array<TableColumn<CommitmentRow>>)
      : []),
  ];

  return (
    <Table scrollable rows={previouslyClosedCommitments} columns={columns} />
  );
};

export default PreviouslyClosedCommitmentsTable;
